import { makePostRequest } from "helpers/APIServiceHelper";
import { findSiteName, readCookie } from "helpers/Helper";

// Post function with timeout and error handling
export async function Post(type, params, handleSuccess, handleError, method) {
  const { data, siteId } = params;
  const baseUrl = process.env.APICLOUDFRONTURL;
  const { url } = makePostRequest({ type, siteId }); // This generates the specific URL used for the POST request

  try {
    const postHeaders = getHeaders(type, siteId); // Optimized headers generation

    const response = await fetchWithTimeout(`${baseUrl}/${url}`, {
      method: method || "POST",
      headers: postHeaders,
      body: JSON.stringify(data),
    });
    if (response.ok) {
      let res = await parseResponse(response);
      if (type === "SignUp" || type === "Login") {
        let token = response.headers.get("X-Auth-Token");
        token ? handleSuccess({ res, token }) : handleError(res);
      } else if (type == "UnSubscribe") {
        if (res && (res.status === 200 || res.status === 201)) {
          handleSuccess(res);
        } else {
          handleError(res);
        }
      } else if (!handleSuccess) {
        return res;
      } else {
        if (res) {
          handleSuccess(res);
        } else {
          handleError(res);
        }
      }
    } else {
      const errorRes = await parseResponse(response); // Parse error response
      handleError(errorRes);
    }
  } catch (err) {
    console.error("Error during Post request to URL:", `${baseUrl}/${url}`, err);
    handleError({
      message: "An error occurred during the request",
      error: err,
    });
  }
}

// Helper function for timeout
const fetchWithTimeout = async (url, options, timeout = 90000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  const response = await fetch(url, { ...options, signal: controller.signal });
  clearTimeout(timeoutId);
  return response;
};

// Optimized headers function
const getHeaders = (type, siteId) => {
  const baseHeaders = {
    "Content-Type": "application/json",
  };

  if (type === "LoginV2") {
    const siteName = findSiteName(siteId);
    const origin =
      process.env.ORION_ENV === "development" ? `http://${siteName}` : `https://${siteName}`;
    return {
      ...baseHeaders,
      origin,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    };
  }

  return baseHeaders;
};

// Safe JSON parsing function
const parseResponse = async (response) => {
  let res;
  try {
    res = await response.json(); // Try parsing the response JSON
  } catch (err) {
    console.error("Failed to parse JSON:", err);
    res = { error: "Invalid JSON response" }; // Return default object on failure
  }
  return res;
};

export async function MakeStepAPIPostReq(url, data, includeApiKey) {
  try {
    const baseUrl = process.env.STEPAPICLOUDFRONTURL;
    const ormtoken = readCookie("ormtoken");
    const reqBody = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        Expires: "0",
        "x-auth-token": ormtoken,
      },
      body: JSON.stringify(data),
    };
    if (includeApiKey) {
      reqBody.headers["x-api-key"] = process.env.ORM_CONTENT_API_KEY;
    }
    const response = await fetch(`${baseUrl}/${url}`, reqBody);
    let res = await response.json();
    return res;
  } catch (err) {
    return err;
  }
}

/**
 * Common function to make API requests with retry mechanism.
 * @param {string} url - The API endpoint to call.
 * @param {Object} options - Fetch options (method, headers, body, etc.).
 * @param {number} retries - Number of retries in case of failure (default: 3).
 * @param {number} delay - Delay between retries in milliseconds (default: 2000).
 * @returns {Promise<Response>} - Returns the parsed JSON response from the API.
 */
export const apiRequestWithRetry = async (url, options = {}, retries = 3, delay = 2000) => {
  const baseUrl = process.env.APICLOUDFRONTURL;

  const sanitizedUrl = url.startsWith("/") ? url.substring(1) : url;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      let response = await fetch(`${baseUrl}/${sanitizedUrl}`, options);
      if (!response.ok && response.status >= 500) {
        throw new Error(
          `HTTP error! Status: ${response.status} - ${response.statusText} - URL: ${sanitizedUrl}`
        );
      }

      const jsonResponse = await response.json();
      return jsonResponse;
    } catch (error) {
      if (attempt === retries) {
        console.error(`Request failed after ${retries} attempts:`, sanitizedUrl, error);
        throw error;
      }
      console.warn(`Attempt ${attempt} failed. Retrying in ${delay / 1000} seconds...`, error);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};
